import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { ConsentServiceName, ConsentTool, SkipLinkConfig, SkipLinksDirective } from '@gs/shared';
import {
  ConfigService,
  CookiebannerService,
  EnvironmentService,
  InformationService,
  LANGUAGE_COOKIE_NAME,
  LanguageService,
  LocalStorageService,
  PageService,
  ScriptService,
  SystemService
} from '@gs/shared/services';
import { FooterComponent } from '@gs/shared/template/app/components/footer/footer.component';
import { HeaderComponent } from '@gs/shared/template/app/components/header/header.component';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { fromEvent, interval, startWith, Subscription, tap } from 'rxjs';
import { EditorToolbarComponent } from '../../components/editor-toolbar/editor-toolbar.component';
import { IN_APP_COOKIE_KEY } from '../../interceptors/plugin/plugin.interceptor';
import { RightsService } from '../../services/rights.service';

const MINUTES_INTERVAL = 5;

@Component({
  selector: 'app-default-layout',
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent, SkipLinksDirective, EditorToolbarComponent],
  providers: [CookiebannerService],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- gsSkipLinks renders the global, translated skip links (content + navigation) as the first focusable elements -->
    <!-- gs-responsive-container: the page shell is the query container for header, page content and footer
         (see responsive-values.css). Full width, so the breakpoint follows the viewport. Overlays that move
         themselves to <body> (gs-modal, gs-alert, checkout-modal, gs-sidebar) leave the shell and carry their own. -->
    <div class="gs-responsive-container relative flex min-h-screen flex-col justify-start" [gsSkipLinks]="skipLinks">
      @if (showMasterLayout()) {
        <gs-header class="z-50 w-full max-w-full" [scrollPosition]="_scrollTop()"></gs-header>
      }
      <main id="main-content" tabindex="-1" class="w-full max-w-full">
        <router-outlet></router-outlet>
      </main>
      @if (showMasterLayout()) {
        <gs-footer class="z-30 mt-auto w-full max-w-full overflow-hidden"></gs-footer>
      }
      <!-- Shortcuts to Designer and backend, the chunk only loads for editors -->
      @if (showEditorToolbar()) {
        @defer (on idle) {
          <app-editor-toolbar />
        }
      }
    </div>
  `
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  // Services
  public readonly config = inject(ConfigService);
  public readonly systemService = inject(SystemService);
  protected readonly pageService = inject(PageService);
  private readonly cookieBannerService = inject(CookiebannerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly envService = inject(EnvironmentService);
  private readonly informationService = inject(InformationService);
  private readonly languageService = inject(LanguageService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly ngZone = inject(NgZone);
  private readonly rightsService = inject(RightsService);
  private readonly scriptService = inject(ScriptService);
  private readonly ssrCookieService = inject(SsrCookieService);

  // Properties & Signals
  /** Global skip links (content + navigation), rendered and translated by the gsSkipLinks directive */
  protected readonly skipLinks: SkipLinkConfig[] = [
    { target: '#main-content', labelKey: 'header.skipToContent' },
    { target: '#site-navigation', labelKey: 'header.skipToNavigation' }
  ];
  /**
   * Header and footer form the website shell. They are dropped for pages laid out as 'LayoutEmpty' and
   * for pages delivered into the app WebView (CS4-2372): the WebView brings its own title bar, and the
   * shell's links point at the website and would lead the user out of the app.
   */
  protected readonly showMasterLayout: Signal<boolean> = computed(() => {
    if (this.systemService.fullPage) return false;
    const page = this.pageService.pageSignal();
    if (!page?.masterView || page.inApp) return false;
    return page.masterView.layout !== 'LayoutEmpty';
  });
  /** Editor shortcuts only on website pages, not in the app WebView or on full-page views */
  protected readonly showEditorToolbar: Signal<boolean> = computed(() => {
    if (!this.rightsService.isEditor() || this.systemService.fullPage || this.runsInAppWebView()) return false;
    const page = this.pageService.pageSignal();
    return !page?.inApp && !page?.isPlugin;
  });
  /** Holds the scrollTop of the pageContent */
  _scrollTop: WritableSignal<number> = signal(0);
  /** Holds all subscriptions and cleans them up on destroy */
  subscriptions: Subscription[] = [];
  /** Guards the cookie banner against double-initialization when both init and config signals update */
  private cookieBannerInitialized = false;

  constructor() {
    effect(() => {
      // Injects the backend-built page snippets on every navigation: the global setup
      // (GTM container, GA4 gtag.js, consent defaults) is de-duplicated and loads once,
      // the per-page snippets are injected on the page they belong to.
      this.scriptService.injectPageScripts(this.pageService.pageSignal()?.metaData);
    });

    // Server and browser: the server renders the menus and hands its init data over to the browser
    this.systemService.log('[DefaultLayout] Initializing InformationService in constructor', new Date().toISOString());
    this.subscriptions.push(
      this.informationService.initialize().subscribe({
        next: (data) => {
          this.systemService.log('[DefaultLayout] InformationService initialized', {
            hasMenus: !!data?.menus?.['cs4-menu'],
            menuLength: data?.menus?.['cs4-menu']?.length,
            timestamp: new Date().toISOString()
          });
        },
        error: (err) => {
          console.error('[DefaultLayout] InformationService initialization failed', err);
        }
      })
    );

    effect(() => {
      // Initialize cookie banner once both InformationService.init() and ConfigService.config()
      // are populated. CookiebannerService.applyButtonStyleVariables() reads from configService.config(),
      // so initializing before the tenant config has loaded would apply default colors instead of
      // the tenant-specific ones — visible to first-time visitors with an empty localStorage.
      const initData = this.informationService.init();
      const tenantConfig = this.config.config();
      if (
        !this.cookieBannerInitialized &&
        initData?.consentTool === ConsentTool.CookieBanner &&
        tenantConfig &&
        this.systemService.browser &&
        !this.systemService.isCrawler()
      ) {
        this.cookieBannerInitialized = true;
        const serviceNames = Object.keys(initData.consentInfo ?? {}) as ConsentServiceName[];
        const inAppWebView = this.runsInAppWebView();
        this.scriptService.loadComponentScripts(serviceNames).subscribe({
          complete: () => {
            console.debug('[DefaultLayout] All component scripts loaded for Cookie Banner');
            import('vanilla-cookieconsent').then((m) => {
              const cookieBanner = this.cookieBannerService.getCookieConsentConfig(
                initData.companyInfo,
                initData.linkTargets,
                initData.consentInfo
              );
              // In the app WebView the consent tool is set up as usual but stays silent (CS4-2575): only the
              // dialog is suppressed, not the initialization. Categories and services stay registered and
              // `ConsentService.consentInfo` gets filled, so the consent placeholders of embedded content
              // (Google Maps, YouTube, Elfsight) remain operable — skipping the initialization altogether
              // would leave their accept button disabled forever.
              m.run(inAppWebView ? { ...cookieBanner, autoShow: false, disablePageInteraction: false } : cookieBanner);
            });
          },
          error: (err) => {
            console.error('[DefaultLayout] Failed to load one or more component scripts for Cookie Banner', err);
            this.cookieBannerInitialized = false; // Reset flag to allow retrying script loading on next config/init update
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.initApp();
    if (this.systemService.browser) {
      // Setup scroll event listener with throttling
      fromEvent(window, 'scroll')
        .pipe(
          startWith(window.scrollY),
          takeUntilDestroyed(this.destroyRef),
          tap((response: any) => {
            if (typeof response === 'number') {
              this._scrollTop.set(response);
            } else {
              this._scrollTop.set(response.target?.scrollTop ?? window.scrollY);
            }
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // Methods
  /**
   * Is the page rendered inside an app WebView (Loyalty-/B2B-App)?
   *
   * Such a page must not show the website cookie banner (CS4-2575): the app collects consent itself, so the
   * banner is a second, foreign dialog that the user did not expect and that blocks the page until dismissed.
   * Only the dialog is suppressed (`autoShow: false`); the consent tool itself is initialized as usual, so
   * consent-bound content stays operable and, without consent, no tracking script fires (`hasCookie()` stays
   * false) — the privacy-safe default.
   *
   * Read from the `inapp` cookie the app writes before the first navigation (see {@link IN_APP_COOKIE_KEY}).
   * Deliberately not `page.inApp`: that flag only arrives with the page response, while the banner is already
   * initialized from the init/config signals — the banner would flash up before it could be suppressed.
   */
  private runsInAppWebView(): boolean {
    return !!this.ssrCookieService.get(IN_APP_COOKIE_KEY);
  }

  /**
   * Server-side initialization of the app.
   */
  private async initApp() {
    console.log('## CloudFront Version:', this.envService.env?.COMMIT_HASH);

    // Initialize language service for both SSR and browser
    await this.languageService.initialize();

    // Server: config from the request context, browser: the config the server rendered with or the backend config
    await this.config.initialize();

    if (this.systemService.browser) {
      // Check if the app key in the environment matches the app key in the configuration
      const currentConfig = this.config.config();
      if (currentConfig && this.envService.env && this.envService.env.APP_KEY != currentConfig.appKey) {
        console.warn(
          'App-Key mismatch',
          `The app-key in the environment (${this.envService.env?.APP_KEY}) does not match the app key in the configuration (${currentConfig.appKey})`,
          'Switching to new app'
        );
        this.config.deleteConfig();
        this.informationService.clearCache();
        this.ssrCookieService.delete('gs.ocs.auth', '/');
        this.ssrCookieService.delete('gs.ocs.session.id', '/');
        this.ssrCookieService.delete('gs.ocs.identifier.user', '/');
        this.ssrCookieService.delete('gs.ocs.identifier.basket', '/');
        this.ssrCookieService.delete(LANGUAGE_COOKIE_NAME, '/');
        this.localStorageService.remove('app-language');
        window.location.reload();
        return;
      }
      // Create the polling interval for the backend config and listen for changes. Outside the zone: a pending
      // interval keeps the app from ever becoming stable, which blocks the hydration (NG0506).
      const pollingInterval =
        currentConfig?.pollingInterval && currentConfig.pollingInterval >= 1 ? currentConfig?.pollingInterval : MINUTES_INTERVAL;
      this.subscriptions.push(
        this.ngZone.runOutsideAngular(() =>
          interval(1000 * 60 * pollingInterval).subscribe(() =>
            this.ngZone.run(async () => {
              const data = await this.config.loadBackendConfig();
              this.systemService.log('[AppComponent] Automatically loaded config from backend', data);
            })
          )
        )
      );
    }
  }
}
